import DuplicateSlicesDialog from './components/DuplicateSlicesDialog';
import EditorHeader from './components/EditorHeader';
import EditorLoadingSkeleton from './components/EditorLoadingSkeleton';
import MetadataCard from './components/MetadataCard';
import SliceCardWrapper from './components/SliceCardWrapper';
import SliceMinimap from './components/SliceMinimap';
import SwitchSliceTypeDialog from './components/SwitchSliceTypeDialog';
import ValidationAlert from './components/ValidationAlert';
import { DataLoaderProvider } from './providers/DataLoaderProvider';
import { FormStateProvider, useFormControls, useFormState } from './providers/FormStateProvider';

/**
 * Root component for the Storefront Configuration Editor
 * Provides necessary context providers for data loading and form state management
 *
 * @returns {JSX.Element} StorefrontConfigEditor component wrapped with required providers
 */
function StorefrontConfigEditor() {
    return (
        <DataLoaderProvider>
            <FormStateProvider>
                <Editor />
            </FormStateProvider>
        </DataLoaderProvider>
    );
}

/**
 * Root editor component that handles form submission and renders the editor interface
 * Uses form state and controls from context to manage configuration data
 *
 * @returns {JSX.Element|null} Editor component or null if form data is not loaded
 */
function Editor() {
    const { formData } = useFormState();
    const {
        duplicateSlicesDialog,
        publishConfig,
        saveConfig,
        sellableBundles,
        sellableItems,
        switchSliceTypeDialog,
        toggleDuplicateSlicesDialog,
        toggleSwitchSliceTypeDialog,
        updateSlice,
        validationErrors,
    } = useFormControls();

    const handleSubmit = async (e) => {
        e.preventDefault();

        const submitButtonName = e.nativeEvent.submitter.name;

        if (submitButtonName === 'save') {
            await saveConfig(e, formData);
        } else if (submitButtonName === 'publish') {
            await publishConfig(e, formData);
        }
    };

    if (!formData || !sellableBundles || !sellableItems) {
        return <EditorLoadingSkeleton />;
    }

    return (
        <>
            <form id="storefront-config-form" onSubmit={handleSubmit}>
                <EditorHeader />
                <section className="flex w-full gap-4 max-w-7xl mx-auto">
                    <SliceMinimap />
                    <div className="flex flex-col gap-6 pt-4 pb-12 w-full min-w-0">
                        <ValidationAlert errors={validationErrors} />
                        <MetadataCard formData={formData} />
                        {formData.slices.map((slice, index) => (
                            <SliceCardWrapper key={slice.id} index={index} slice={slice} />
                        ))}
                    </div>
                </section>
            </form>
            <DuplicateSlicesDialog
                onOpenChange={() => toggleDuplicateSlicesDialog(null)}
                open={duplicateSlicesDialog.open}
                sourceConfig={formData}
                sourceSlice={duplicateSlicesDialog.sourceSlice}
            />
            <SwitchSliceTypeDialog
                open={switchSliceTypeDialog.open}
                onOpenChange={() => toggleSwitchSliceTypeDialog(null)}
                currentDisplayType={switchSliceTypeDialog.slice?.displayType}
                imageUrl={switchSliceTypeDialog.slice?.imageUrl}
                onConfirm={(displayType) => updateSlice(switchSliceTypeDialog.index, { displayType })}
            />
        </>
    );
}

export default StorefrontConfigEditor;
